import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import useLiterals from 'utils/hooks/useLiterals';
import { ROUTE_PATH } from 'routes';
import {
  hasDigitalLoanInternalPreview,
  isDigitalLoanProductionGateActive,
} from 'utils/digitalLoanPreviewAccess';
import { DIGITAL_LOAN_STEPS, REJECTION_TYPES } from 'constants/digitalLoan';
import DIGITAL_LOAN_LEGAL_DOCUMENTS from 'constants/digitalLoanLegalDocuments';
import {
  MOCK_LOAN_RATE,
  PHASE2_MOCK_SCENARIOS,
  MANUAL_CATALOG_LEVELS,
  MANUAL_MODEL_CODE,
  MANUAL_OTHER_NAME,
  OTHER_OPTION_ID,
} from 'constants/digitalLoanPhase2';
import {
  LOCAL_VISIT_STATUS,
  VISIT_MODIFY_CUTOFF_HOURS,
} from 'constants/digitalLoanPhase3';
import { USER_LEVEL } from 'constants/global';
import { STEPS as REGISTRATION_STEPS } from 'constants/registration';
import {
  acceptPreapproval,
  calculateNewLoanAmount,
  checkBlackList,
  getArticles,
  getBranches,
  getDigitalLoanStatus,
  getFamilies,
  getModels,
  getSubFamilies,
  cancelVisit,
  rescheduleVisit,
  runAmountAndScoring,
  scheduleVisit,
  startDigitalLoanApplication,
  submitApplicabilityQuestionnaire,
  submitGuarantee,
  validateCoordinates,
} from 'modules/digitalLoan/actions';
import { estimatedValueStateFromModel } from 'modules/digitalLoan/catalogNormalize';
import { createEmptyPhotos } from 'modules/digitalLoan/mocks';
import {
  buildScheduledVisitAt,
  isVisitWithinHours,
  resolveScheduledVisitAt,
} from 'modules/digitalLoan/visitSchedule';
import IntroScreen from './components/IntroScreen';
import PermissionsScreen, {
  REQUIRED_PERMISSION_KEYS,
} from './components/PermissionsScreen';
import LegalDocumentViewer from './components/LegalDocumentViewer';
import LocationScreen from './components/LocationScreen';
import ApplicabilityLoanScreen from './components/ApplicabilityLoanScreen';
import ApplicabilityAddressScreen from './components/ApplicabilityAddressScreen';
import ApplicabilityIdentityScreen from './components/ApplicabilityIdentityScreen';
import BiometricVerificationModal from './components/BiometricVerificationModal';
import VerificationLoaderModal from './components/VerificationLoaderModal';
import RejectionSheet from './components/RejectionSheet';
import GuaranteeAvailabilityScreen from './components/GuaranteeAvailabilityScreen';
import CatalogSelectionScreen from './components/CatalogSelectionScreen';
import ModelDetailScreen from './components/ModelDetailScreen';
import ManualCatalogDetailScreen from './components/ManualCatalogDetailScreen';
import PhotoRequirementsScreen from './components/PhotoRequirementsScreen';
import PhotoCaptureScreen from './components/PhotoCaptureScreen';
import PhotoReviewScreen from './components/PhotoReviewScreen';
import ValidationFailedScreen from './components/ValidationFailedScreen';
import InsufficientValueScreen from './components/InsufficientValueScreen';
import MissingOnboardingScreen from './components/MissingOnboardingScreen';
import NotPreapprovedScreen from './components/NotPreapprovedScreen';
import PreOfferScreen from './components/PreOfferScreen';
import VisitStatusScreen from './components/VisitStatusScreen';
import Phase4Wizard from './components/Phase4Wizard';
import {
  getPermittedStep,
  getPreviousStep,
  isPhase2Step,
  isPhase3Step,
  isPhase4Step,
} from './utils/flowGuards';
import { createInitialQuestionnaire } from './utils/questionnaireModel';
import { emptyAddressLookupPatch } from './utils/addressLookup';
import {
  clearManualCatalogFromLevel,
  createManualOtherItem,
  emptyManualCatalogValues,
  getManualCatalogBackStep,
  isManualCatalogDetailLevel,
  isUiOtherOption,
  normalizeCollateralText,
  patchManualCatalogValues,
  shouldSkipCatalogEndpoint,
} from './utils/catalogOtherOption';
import {
  clearGuaranteeProductSelection,
  createInitialPhase2State,
  mapScoringStatusToStep,
  resolveDisplayedGuaranteeImage,
  resolveGuaranteeDescription,
  resolveGuaranteeDisplayName,
} from './utils/phase2MockMapping';
import {
  getPhase3Snapshot,
  savePhase3Snapshot,
} from './utils/phase3Snapshot';
import {
  buildVisitCalendar,
  mergePreOfferDisplay,
  normalizePhase3Status,
  resolveMaxReschedules,
} from './utils/phase3Display';
import { resolveDigitalLoanEntryStep } from './utils/phase3Routing';
import { isPhase4FeStep } from './utils/phase4Routing';
import { resolveQuestionnaireSubmitErrorMessage } from './utils/questionnaireSubmitError';
import * as verifyBiometric from './utils/verifyBiometric';
import './styles.scss';

const INITIAL_PERMISSIONS = {
  privacy: false,
  terms: false,
  bureau: false,
  imss: false,
};

const INITIAL_FLOW_PROGRESS = {
  introCompleted: false,
  applicationStarted: false,
  blacklistPassed: false,
  coordinatesValidated: false,
  loanStepCompleted: false,
  addressStepCompleted: false,
  questionnaireCompleted: false,
  guaranteeFlowUnlocked: false,
  isModifyingTerm: false,
  phase3Resume: false,
  phase4Resume: false,
};

const PHASE3_ENTRY_STEPS = [
  DIGITAL_LOAN_STEPS.MISSING_ONBOARDING,
  DIGITAL_LOAN_STEPS.PRE_OFFER,
  DIGITAL_LOAN_STEPS.VISIT_STATUS,
  DIGITAL_LOAN_STEPS.NOT_PREAPPROVED,
  DIGITAL_LOAN_STEPS.INSUFFICIENT_VALUE,
  DIGITAL_LOAN_STEPS.VALIDATION_FAILED,
];

const DigitalLoan = () => {
  const { step } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const newApplicationIntentRef = useRef(
    location.state?.digitalLoanIntent === 'new',
  );
  const dispatch = useDispatch();
  const literals = useLiterals('digitalLoan');
  const { user } = useSelector((state) => state.session || {});
  const isRobustOnboardingComplete = user?.level === USER_LEVEL.COMPLETE;
  const isPhase3Entry = PHASE3_ENTRY_STEPS.includes(step);
  const isPhase4Entry = isPhase4FeStep(step);
  const needsStatusHydration = isPhase3Entry || isPhase4Entry;
  const initialSnapshot = getPhase3Snapshot();
  const stepRef = useRef(step);
  stepRef.current = step;

  const [flowProgress, setFlowProgress] = useState(() => ({
    ...INITIAL_FLOW_PROGRESS,
    phase3Resume: isPhase3Entry && !newApplicationIntentRef.current,
    phase4Resume: isPhase4Entry && !newApplicationIntentRef.current,
  }));
  const [questionnaire, setQuestionnaire] = useState(createInitialQuestionnaire);
  const [permissions, setPermissions] = useState(INITIAL_PERMISSIONS);
  const legalTriggerRef = useRef(null);
  const [activeLegalDocument, setActiveLegalDocument] = useState(null);
  const [applicationId, setApplicationId] = useState(
    () => ((isPhase3Entry || isPhase4Entry) && !newApplicationIntentRef.current
      ? initialSnapshot?.applicationId || null
      : null),
  );
  const [phase2, setPhase2] = useState(createInitialPhase2State);
  const [phase3Snapshot, setPhase3Snapshot] = useState(
    () => (isPhase3Entry ? initialSnapshot : null),
  );
  const [phase3Status, setPhase3Status] = useState(null);
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [allowManualModel, setAllowManualModel] = useState(false);
  const [showBiometricModal, setShowBiometricModal] = useState(false);
  const [biometricError, setBiometricError] = useState(false);
  const [showVerificationLoader, setShowVerificationLoader] = useState(false);
  const [rejectionType, setRejectionType] = useState(null);
  const [technicalError, setTechnicalError] = useState(null);
  const [isValidatingCoordinates, setIsValidatingCoordinates] = useState(false);
  const [isSubmittingQuestionnaire, setIsSubmittingQuestionnaire] = useState(false);
  const [isSubmittingPhase2, setIsSubmittingPhase2] = useState(false);
  const [isSubmittingPhase3, setIsSubmittingPhase3] = useState(false);

  const mountedRef = useRef(true);
  const startRequestRef = useRef(false);
  const biometricRequestRef = useRef(0);
  const coordinateRequestRef = useRef(false);
  const questionnaireSubmitRef = useRef(false);
  const phase2RequestRef = useRef(false);
  const phase3RequestRef = useRef(false);
  const phase4AlignedRef = useRef(false);

  const permittedStep = useMemo(
    () => getPermittedStep(step, flowProgress),
    [step, flowProgress],
  );

  const questionnaireLiterals = useMemo(
    () => ({
      loan: {
        ...literals.questionnaire.loan,
        next: literals.questionnaire.common.next,
        back: literals.questionnaire.common.back,
      },
      address: {
        ...literals.questionnaire.address,
        next: literals.questionnaire.common.next,
        back: literals.questionnaire.common.back,
      },
      identity: {
        ...literals.questionnaire.identity,
        next: literals.questionnaire.common.next,
        back: literals.questionnaire.common.back,
      },
    }),
    [literals.questionnaire],
  );

  useEffect(() => {
    verifyBiometric.snapshotDevBiometricModeFromUrl();
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      isDigitalLoanProductionGateActive()
      && !hasDigitalLoanInternalPreview()
    ) {
      navigate(ROUTE_PATH.HOME, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (permittedStep !== step) {
      navigate(ROUTE_PATH.setDigitalLoan(permittedStep), { replace: true });
    }
  }, [navigate, permittedStep, step]);

  useEffect(() => {
    if (
      permittedStep !== DIGITAL_LOAN_STEPS.PERMISSIONS
      && activeLegalDocument
    ) {
      setActiveLegalDocument(null);
    }
  }, [permittedStep, activeLegalDocument]);

  useEffect(() => {
    if (activeLegalDocument) return undefined;
    const trigger = legalTriggerRef.current;
    legalTriggerRef.current = null;
    if (trigger instanceof HTMLElement && trigger.isConnected) {
      trigger.focus();
    }
    return undefined;
  }, [activeLegalDocument]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
    if (typeof document !== 'undefined') {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [permittedStep]);

  useEffect(() => {
    if (!needsStatusHydration || newApplicationIntentRef.current) return undefined;
    let cancelled = false;
    dispatch(getDigitalLoanStatus())
      .then((result) => {
        if (cancelled || !mountedRef.current) return;
        setPhase3Status(result);
        const appId = result?.activeApplication?.applicationId;
        if (appId) {
          setApplicationId((current) => current || appId);
          const stored = getPhase3Snapshot(appId);
          if (stored && isPhase3Entry) setPhase3Snapshot(stored);
        }
        if (!phase4AlignedRef.current && isPhase4FeStep(stepRef.current)) {
          phase4AlignedRef.current = true;
          const resolved = resolveDigitalLoanEntryStep({
            currentStep: result?.activeApplication?.currentStep,
            currentPhase: result?.activeApplication?.currentPhase,
            status: result,
            snapshot: getPhase3Snapshot(appId),
            isRobustOnboardingComplete,
          });
          if (
            isPhase4FeStep(resolved)
            && resolved !== stepRef.current
          ) {
            navigate(ROUTE_PATH.setDigitalLoan(resolved), { replace: true });
          }
        }
      })
      .catch(() => {
        if (!cancelled && mountedRef.current) {
          setPhase3Status(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    isPhase3Entry,
    isRobustOnboardingComplete,
    navigate,
    needsStatusHydration,
  ]);

  const updateQuestionnaire = useCallback((patch) => {
    setQuestionnaire((current) => ({ ...current, ...patch }));
  }, []);

  const updateQuestionnaireAddress = useCallback((patch) => {
    setQuestionnaire((current) => ({
      ...current,
      address: { ...current.address, ...patch },
    }));
  }, []);

  const updatePhase2 = useCallback((patch) => {
    setPhase2((current) => ({ ...current, ...patch }));
  }, []);

  const closeBiometricModal = useCallback(() => {
    biometricRequestRef.current += 1;
    setShowBiometricModal(false);
    setBiometricError(false);
  }, []);

  const resetFlowToHome = useCallback(() => {
    setRejectionType(null);
    setApplicationId(null);
    setFlowProgress(INITIAL_FLOW_PROGRESS);
    setQuestionnaire(createInitialQuestionnaire());
    setPermissions(INITIAL_PERMISSIONS);
    setPhase2(createInitialPhase2State());
    setCatalogItems([]);
    navigate(ROUTE_PATH.HOME, { replace: true });
  }, [navigate]);

  const selectOtherPledge = useCallback(() => {
    setPhase2((current) => clearGuaranteeProductSelection(current));
    setFlowProgress((current) => ({
      ...current,
      isModifyingTerm: false,
      guaranteeFlowUnlocked: true,
    }));
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.GUARANTEE_FAMILY));
  }, [navigate]);

  const runBlacklistValidation = useCallback(
    async (createdApplicationId) => {
      try {
        const result = await dispatch(checkBlackList(createdApplicationId));
        if (!mountedRef.current) return;

        setShowVerificationLoader(false);

        if (result?.isBlocked) {
          setRejectionType(REJECTION_TYPES.BLACKLIST);
          return;
        }

        setFlowProgress((current) => ({ ...current, blacklistPassed: true }));
        navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.LOCATION));
      } catch (error) {
        if (!mountedRef.current) return;
        setShowVerificationLoader(false);
        setTechnicalError(error);
      }
    },
    [dispatch, navigate],
  );

  const startApplicationFlow = useCallback(async () => {
    if (startRequestRef.current) return;
    startRequestRef.current = true;
    setShowVerificationLoader(true);

    try {
      const startResult = await dispatch(
        startDigitalLoanApplication(permissions),
      );
      if (!mountedRef.current) return;

      if (!startResult?.applicationId) {
        throw new Error('Invalid start response');
      }

      setApplicationId(startResult.applicationId);
      setFlowProgress((current) => ({
        ...current,
        applicationStarted: true,
      }));

      await runBlacklistValidation(startResult.applicationId);
    } catch (error) {
      if (!mountedRef.current) return;
      setShowVerificationLoader(false);
      setTechnicalError(error);
    } finally {
      startRequestRef.current = false;
    }
  }, [dispatch, permissions, runBlacklistValidation]);

  const loadCatalog = useCallback(
    async (loader, { onLoaded } = {}) => {
      setCatalogLoading(true);
      try {
        const items = await dispatch(loader);
        if (!mountedRef.current) return;
        const list = items || [];
        setCatalogItems(list);
        if (onLoaded) onLoaded(list, null);
      } catch (error) {
        if (!mountedRef.current) return;
        setTechnicalError(error);
        setCatalogItems([]);
        if (onLoaded) onLoaded([], error);
      } finally {
        if (mountedRef.current) {
          setCatalogLoading(false);
        }
      }
    },
    [dispatch],
  );

  const skipSubFamiliesCatalog = shouldSkipCatalogEndpoint(
    phase2,
    'subFamilies',
  );
  const skipArticlesCatalog = shouldSkipCatalogEndpoint(phase2, 'articles');
  const skipBrandsCatalog = shouldSkipCatalogEndpoint(phase2, 'brands');
  const skipModelsCatalog = shouldSkipCatalogEndpoint(phase2, 'models');

  useEffect(() => {
    if (permittedStep === DIGITAL_LOAN_STEPS.GUARANTEE_FAMILY) {
      setAllowManualModel(false);
      loadCatalog(getFamilies());
    }
    if (
      permittedStep === DIGITAL_LOAN_STEPS.GUARANTEE_SUBFAMILY
      && phase2.selectedFamily?.id
      && !skipSubFamiliesCatalog
    ) {
      setAllowManualModel(false);
      loadCatalog(getSubFamilies(phase2.selectedFamily.id));
    }
    if (
      permittedStep === DIGITAL_LOAN_STEPS.GUARANTEE_ARTICLE
      && phase2.selectedSubFamily?.id
      && !skipArticlesCatalog
    ) {
      setAllowManualModel(false);
      loadCatalog(getArticles(phase2.selectedSubFamily.id));
    }
    if (
      permittedStep === DIGITAL_LOAN_STEPS.GUARANTEE_BRAND
      && phase2.selectedArticle?.id
      && !skipBrandsCatalog
    ) {
      setAllowManualModel(false);
      const article = phase2.selectedArticle;
      const idFamiliaSubFamilia = article.idFamiliaSubFamilia ?? phase2.selectedSubFamily?.id;
      const { idSubFamiliaTipoArticulo } = article;
      if (idFamiliaSubFamilia != null && idSubFamiliaTipoArticulo != null) {
        loadCatalog(
          getBranches({
            idFamiliaSubFamilia,
            idSubFamiliaTipoArticulo,
          }),
        );
      }
    }
    if (
      permittedStep === DIGITAL_LOAN_STEPS.GUARANTEE_MODEL
      && phase2.selectedArticle?.id
      && phase2.selectedBrand?.id
      && !skipModelsCatalog
    ) {
      const article = phase2.selectedArticle;
      const brand = phase2.selectedBrand;
      const idFamiliaSubFamilia = article.idFamiliaSubFamilia ?? phase2.selectedSubFamily?.id;
      setAllowManualModel(false);
      const onModelsLoaded = (list, error) => {
        if (error) {
          setAllowManualModel(false);
          return;
        }
        const empty = list.length === 0;
        setAllowManualModel(empty);
        setPhase2((current) => {
          if (!current.selectedModel) return current;
          if (empty) {
            return {
              ...current,
              selectedModel: null,
              estimatedValue: '',
              estimatedValueLocked: false,
            };
          }
          if (current.selectedModel.id === MANUAL_MODEL_CODE) {
            return {
              ...current,
              selectedModel: null,
              estimatedValue: '',
              estimatedValueLocked: false,
            };
          }
          const stillValid = list.some(
            (item) => item.id === current.selectedModel.id,
          );
          if (stillValid) return current;
          return {
            ...current,
            selectedModel: null,
            estimatedValue: '',
            estimatedValueLocked: false,
          };
        });
      };
      if (
        brand?.id
        && article.idSubFamiliaTipoArticulo != null
        && idFamiliaSubFamilia != null
      ) {
        loadCatalog(
          getModels({
            idMarca: brand.id,
            idTipoArticulo: article.id,
            idSubfamiliaTipoArticulo: article.idSubFamiliaTipoArticulo,
            idFamiliaSubFamilia,
            familyCode: phase2.selectedFamily?.id,
          }),
          { onLoaded: onModelsLoaded },
        );
      }
    }
  }, [
    loadCatalog,
    permittedStep,
    skipSubFamiliesCatalog,
    skipArticlesCatalog,
    skipBrandsCatalog,
    skipModelsCatalog,
    phase2.selectedArticle,
    phase2.selectedBrand?.id,
    phase2.selectedFamily?.id,
    phase2.selectedSubFamily?.id,
    phase2.selectedBrand,
  ]);

  const handleIntroContinue = () => {
    setFlowProgress((current) => ({ ...current, introCompleted: true }));
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.PERMISSIONS));
  };

  const handlePermissionsBack = () => {
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.INTRO));
  };

  const handlePermissionChange = (key) => {
    setPermissions((current) => ({ ...current, [key]: !current[key] }));
  };

  const handleOpenLegalDocument = (key) => {
    if (!DIGITAL_LOAN_LEGAL_DOCUMENTS[key]) return;
    legalTriggerRef.current = window.document.activeElement;
    setActiveLegalDocument(key);
  };

  const handleCloseLegalDocument = () => {
    setActiveLegalDocument(null);
  };

  const startBiometricVerification = useCallback(() => {
    const requestId = biometricRequestRef.current + 1;
    biometricRequestRef.current = requestId;
    setBiometricError(false);
    setShowBiometricModal(true);

    verifyBiometric
      .verifyDeviceBiometric()
      .then(() => {
        if (!mountedRef.current || requestId !== biometricRequestRef.current) { return; }
        setShowBiometricModal(false);
        setBiometricError(false);
        startApplicationFlow();
      })
      .catch(() => {
        if (!mountedRef.current || requestId !== biometricRequestRef.current) { return; }
        setBiometricError(true);
      });
  }, [startApplicationFlow]);

  const handlePermissionsAccept = () => {
    const allAccepted = REQUIRED_PERMISSION_KEYS.every(
      (key) => permissions[key],
    );
    if (!allAccepted) return;
    startBiometricVerification();
  };

  const handleBiometricRetry = () => {
    startBiometricVerification();
  };

  const handleBiometricBack = () => {
    closeBiometricModal();
  };

  const handleLocationBack = () => {
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.PERMISSIONS));
  };

  const handleLocationValidate = async (payload) => {
    if (
      isValidatingCoordinates
      || coordinateRequestRef.current
      || !applicationId
    ) {
      return;
    }

    coordinateRequestRef.current = true;
    setIsValidatingCoordinates(true);
    try {
      const result = await dispatch(
        validateCoordinates(
          payload.postalCode,
          payload.latitude,
          payload.longitude,
          applicationId,
        ),
      );

      if (!mountedRef.current) return;

      if (result?.isValid) {
        setQuestionnaire((current) => ({
          ...current,
          locationType: payload.locationOption,
          companyOrBusinessName: payload.companyOrBusinessName || '',
          locationPostalCode: payload.postalCode,
          coordinates: {
            latitude: payload.latitude,
            longitude: payload.longitude,
          },
          address: {
            ...current.address,
            postalCode: payload.postalCode,
            ...(current.address.postalCode !== payload.postalCode
              ? emptyAddressLookupPatch()
              : {}),
          },
        }));
        setFlowProgress((current) => ({
          ...current,
          coordinatesValidated: true,
        }));
        navigate(
          ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.APPLICABILITY_LOAN),
        );
        return;
      }

      setRejectionType(REJECTION_TYPES.LOCATION);
    } catch (error) {
      if (!mountedRef.current) return;
      setTechnicalError(error);
    } finally {
      coordinateRequestRef.current = false;
      if (mountedRef.current) {
        setIsValidatingCoordinates(false);
      }
    }
  };

  const persistPhase3Snapshot = useCallback((patch) => {
    const next = savePhase3Snapshot({
      ...(phase3Snapshot || {}),
      applicationId,
      ...patch,
    });
    if (next) setPhase3Snapshot(next);
    return next;
  }, [applicationId, phase3Snapshot]);

  const applyScoringSuccess = useCallback((scoring, imageOptions = {}) => {
    const calendar = buildVisitCalendar();
    const guaranteeImage = resolveDisplayedGuaranteeImage({
      freshImage: imageOptions.freshGuaranteeImage,
      hasFreshImage: imageOptions.hasFreshGuaranteeImage === true,
      preOfferImage: scoring.preOffer?.guaranteeImage,
      currentImage: phase2.preOffer?.guaranteeImage || phase2.guaranteeImage,
      snapshotImage: phase3Snapshot?.guaranteeImage,
    });
    const preOffer = scoring.preOffer
      ? {
        ...scoring.preOffer,
        guaranteeImage,
        useGuaranteeImage: true,
        availableVisitDates: scoring.preOffer.availableVisitDates?.length
          ? scoring.preOffer.availableVisitDates
          : calendar.availableVisitDates,
        availableVisitTimeSlots: scoring.preOffer.availableVisitTimeSlots?.length
          ? scoring.preOffer.availableVisitTimeSlots
          : calendar.availableVisitTimeSlots,
      }
      : null;

    updatePhase2({
      scoringResult: scoring,
      preOffer,
      selectedVisitDate: null,
      selectedVisitTimeSlot: null,
    });

    let nextStep = mapScoringStatusToStep(scoring.status);
    if (
      nextStep === DIGITAL_LOAN_STEPS.PRE_OFFER
      && !isRobustOnboardingComplete
    ) {
      nextStep = DIGITAL_LOAN_STEPS.MISSING_ONBOARDING;
    }

    if (applicationId && (
      nextStep === DIGITAL_LOAN_STEPS.PRE_OFFER
      || nextStep === DIGITAL_LOAN_STEPS.MISSING_ONBOARDING
    )) {
      persistPhase3Snapshot({
        currentPhase: scoring.scoring?.currentPhase || null,
        currentStep: scoring.scoring?.currentStep || null,
        status: scoring.scoring?.status || null,
        acceptedAmount: preOffer?.amount ?? null,
        suggestedLoanAmount: scoring.scoring?.suggestedLoanAmount
          ?? preOffer?.amount
          ?? null,
        appraisalAmount: scoring.scoring?.appraisalAmount ?? null,
        maxLoanAmount: scoring.scoring?.maxLoanAmount ?? null,
        cappedByMaxAmount: scoring.scoring?.cappedByMaxAmount === true,
        requestedTerm: preOffer?.term ?? questionnaire.paymentTerm,
        paymentFrequency: preOffer?.paymentFrequency || '',
        paymentDay: preOffer?.paymentDay || '',
        guaranteeName: preOffer?.guaranteeName
          || resolveGuaranteeDisplayName(phase2),
        guaranteeDescription: resolveGuaranteeDescription(phase2) || null,
        guaranteeImage,
        scheduledVisitAt: null,
        rescheduleCount: 0,
        maxReschedules: null,
        localVisitStatus: null,
      });
    }

    navigate(ROUTE_PATH.setDigitalLoan(nextStep));
  }, [
    applicationId,
    isRobustOnboardingComplete,
    navigate,
    persistPhase3Snapshot,
    phase2,
    phase3Snapshot?.guaranteeImage,
    questionnaire.paymentTerm,
    updatePhase2,
  ]);

  const runScoringFlow = useCallback(async (imageOptions = {}) => {
    if (phase2RequestRef.current) return;
    phase2RequestRef.current = true;
    setIsSubmittingPhase2(true);

    try {
      const scoringContext = {
        amount: questionnaire.requestedAmount,
        term: questionnaire.paymentTerm,
        rate: MOCK_LOAN_RATE,
        paymentFrequency: questionnaire.paymentFrequency,
        guaranteeName: resolveGuaranteeDisplayName(phase2),
      };

      const scoring = await dispatch(
        runAmountAndScoring({
          applicationId,
          ...scoringContext,
        }),
      );

      if (!mountedRef.current) return;

      applyScoringSuccess(scoring, imageOptions);
    } catch (error) {
      if (!mountedRef.current) return;
      setTechnicalError(error);
    } finally {
      phase2RequestRef.current = false;
      if (mountedRef.current) {
        setIsSubmittingPhase2(false);
      }
    }
  }, [
    applicationId,
    applyScoringSuccess,
    dispatch,
    phase2,
    questionnaire.paymentFrequency,
    questionnaire.paymentTerm,
    questionnaire.requestedAmount,
  ]);

  const handleLoanNext = async () => {
    if (flowProgress.isModifyingTerm) {
      setIsSubmittingPhase2(true);
      try {
        const percentageRate = phase2?.scoringResult?.scoring?.interestRate
          ?? phase2?.scoringResult?.raw?.interestRate
          ?? MOCK_LOAN_RATE;

        const calc = await dispatch(
          calculateNewLoanAmount({
            applicationId,
            newTerm: questionnaire.paymentTerm,
            newRequestedAmount: questionnaire.requestedAmount,
            percentageRate,
          }),
        );

        if (!mountedRef.current) return;

        if (
          calc?.maxLoanAmount != null
          && Number.isFinite(Number(calc.maxLoanAmount))
        ) {
          setQuestionnaire((current) => ({
            ...current,
            requestedAmount: calc.viable
              ? current.requestedAmount
              : Number(calc.maxLoanAmount),
          }));
        }

        if (!mountedRef.current) return;
        setFlowProgress((current) => ({
          ...current,
          isModifyingTerm: false,
        }));
        await runScoringFlow();
      } catch (error) {
        if (!mountedRef.current) return;
        setTechnicalError(error);
      } finally {
        if (mountedRef.current) {
          setIsSubmittingPhase2(false);
        }
      }
      return;
    }

    setFlowProgress((current) => ({ ...current, loanStepCompleted: true }));
    navigate(
      ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.APPLICABILITY_ADDRESS),
    );
  };

  const handleAddressNext = () => {
    setFlowProgress((current) => ({ ...current, addressStepCompleted: true }));
    navigate(
      ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.APPLICABILITY_IDENTITY),
    );
  };

  const handleQuestionnaireSubmit = async () => {
    if (flowProgress.questionnaireCompleted) {
      navigate(
        ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.GUARANTEE_AVAILABILITY),
      );
      return;
    }

    if (
      questionnaireSubmitRef.current
      || isSubmittingQuestionnaire
      || !applicationId
    ) {
      return;
    }

    questionnaireSubmitRef.current = true;
    setIsSubmittingQuestionnaire(true);

    try {
      const result = await dispatch(
        submitApplicabilityQuestionnaire(questionnaire, applicationId),
      );
      if (!mountedRef.current) return;

      if (result?.phase1Completed === false) {
        setRejectionType(REJECTION_TYPES.APPLICABILITY);
        return;
      }

      if (result?.phase1Completed === true) {
        setFlowProgress((current) => ({
          ...current,
          questionnaireCompleted: true,
        }));
        navigate(
          ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.GUARANTEE_AVAILABILITY),
        );
      }
    } catch (error) {
      if (!mountedRef.current) return;

      // eslint-disable-next-line no-console
      console.error('[DigitalLoan] questionnaire submit failed', error);
      setTechnicalError(resolveQuestionnaireSubmitErrorMessage(literals));
    } finally {
      questionnaireSubmitRef.current = false;
      if (mountedRef.current) {
        setIsSubmittingQuestionnaire(false);
      }
    }
  };

  const handleQuestionnaireBack = () => {
    navigate(ROUTE_PATH.setDigitalLoan(getPreviousStep(step)));
  };

  const handleRejectionUnderstood = () => {
    resetFlowToHome();
  };

  const handleTechnicalErrorClose = () => {
    setTechnicalError(null);
  };

  const handleHasGuaranteeProduct = () => {
    updatePhase2({ hasGuaranteeProduct: true });
    setFlowProgress((current) => ({
      ...current,
      guaranteeFlowUnlocked: true,
    }));
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.GUARANTEE_FAMILY));
  };

  const resetCatalogDownstream = {
    selectedModel: null,
    manualCatalogStartLevel: null,
    manualCatalogValues: emptyManualCatalogValues(),
    estimatedValue: '',
    estimatedValueLocked: false,
    guaranteeDescription: null,
  };

  const enterManualCatalog = (level, patch) => {
    setAllowManualModel(false);
    updatePhase2({
      selectedModel: { id: MANUAL_MODEL_CODE, name: '' },
      manualCatalogStartLevel: level,
      manualCatalogValues: emptyManualCatalogValues(),
      productCondition: '',
      estimatedValue: '',
      estimatedValueLocked: false,
      ...patch,
    });
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.GUARANTEE_MODEL));
  };

  const handleFamilySelect = async (family) => {
    if (isUiOtherOption(family)) {
      enterManualCatalog(MANUAL_CATALOG_LEVELS.FAMILY, {
        selectedFamily: createManualOtherItem({ name: '' }),
        selectedSubFamily: createManualOtherItem({ name: MANUAL_OTHER_NAME }),
        selectedArticle: createManualOtherItem({ name: '' }),
        selectedBrand: createManualOtherItem({ name: '' }),
        manualCatalogValues: {
          ...emptyManualCatalogValues(),
          subfamilyName: MANUAL_OTHER_NAME,
        },
      });
      return;
    }
    setAllowManualModel(false);
    updatePhase2({
      selectedFamily: family,
      selectedSubFamily: null,
      selectedArticle: null,
      selectedBrand: null,
      ...resetCatalogDownstream,
    });
    const subFamilies = await dispatch(getSubFamilies(family.id));
    if (!mountedRef.current) return;
    if (subFamilies?.length === 1) {
      if (isUiOtherOption(subFamilies[0])) {
        enterManualCatalog(MANUAL_CATALOG_LEVELS.SUBFAMILY, {
          selectedFamily: family,
          selectedSubFamily: createManualOtherItem({ name: MANUAL_OTHER_NAME }),
          selectedArticle: createManualOtherItem({ name: '' }),
          selectedBrand: createManualOtherItem({ name: '' }),
          manualCatalogValues: {
            ...emptyManualCatalogValues(),
            subfamilyName: MANUAL_OTHER_NAME,
          },
        });
        return;
      }
      updatePhase2({
        selectedFamily: family,
        selectedSubFamily: subFamilies[0],
      });
      navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.GUARANTEE_ARTICLE));
      return;
    }
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.GUARANTEE_SUBFAMILY));
  };

  const handleSubFamilySelect = (subFamily) => {
    if (isUiOtherOption(subFamily)) {
      enterManualCatalog(MANUAL_CATALOG_LEVELS.SUBFAMILY, {
        selectedSubFamily: createManualOtherItem({ name: MANUAL_OTHER_NAME }),
        selectedArticle: createManualOtherItem({ name: '' }),
        selectedBrand: createManualOtherItem({ name: '' }),
        manualCatalogValues: {
          ...emptyManualCatalogValues(),
          subfamilyName: MANUAL_OTHER_NAME,
        },
      });
      return;
    }
    setAllowManualModel(false);
    updatePhase2({
      selectedSubFamily: subFamily,
      selectedArticle: null,
      selectedBrand: null,
      ...resetCatalogDownstream,
    });
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.GUARANTEE_ARTICLE));
  };

  const handleArticleSelect = (article) => {
    if (isUiOtherOption(article)) {
      enterManualCatalog(MANUAL_CATALOG_LEVELS.ARTICLE, {
        selectedArticle: createManualOtherItem({ name: '' }),
        selectedBrand: createManualOtherItem({ name: '' }),
      });
      return;
    }
    setAllowManualModel(false);
    updatePhase2({
      selectedArticle: article,
      selectedBrand: null,
      ...resetCatalogDownstream,
    });
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.GUARANTEE_BRAND));
  };

  const handleBrandSelect = (brand) => {
    if (isUiOtherOption(brand)) {
      enterManualCatalog(MANUAL_CATALOG_LEVELS.BRAND, {
        selectedBrand: createManualOtherItem({ name: '' }),
      });
      return;
    }
    setAllowManualModel(false);
    updatePhase2({
      selectedBrand: brand,
      ...resetCatalogDownstream,
    });
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.GUARANTEE_MODEL));
  };

  const handleModelNext = () => {
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.PHOTO_REQUIREMENTS));
  };

  const handlePhotoConsentChange = (key) => {
    setPhase2((current) => {
      const enabled = !current.photoConsents[key];
      const next = {
        ...current,
        photoConsents: {
          ...current.photoConsents,
          [key]: enabled,
        },
      };
      if (key === 'hasInvoice' && !enabled) {
        next.photos = {
          ...current.photos,
          document: null,
        };
      }
      return next;
    });
  };

  const handleValidateGuarantee = async () => {
    if (phase2RequestRef.current) return;
    phase2RequestRef.current = true;
    setIsSubmittingPhase2(true);

    try {
      const guaranteeResult = await dispatch(
        submitGuarantee({ applicationId, phase2 }),
      );

      if (!mountedRef.current) return;

      const freshGuaranteeImage = guaranteeResult.guaranteeImageUrl || null;
      updatePhase2({
        guaranteeValidationResult: guaranteeResult,
        guaranteeImage: freshGuaranteeImage,
      });
      if (applicationId) {
        persistPhase3Snapshot({
          guaranteeImage: freshGuaranteeImage,
        });
      }

      if (guaranteeResult.status === PHASE2_MOCK_SCENARIOS.VALIDATION_FAILED) {
        navigate(
          ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.VALIDATION_FAILED),
        );
        return;
      }

      if (guaranteeResult.status === PHASE2_MOCK_SCENARIOS.INSUFFICIENT_VALUE) {
        navigate(
          ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.INSUFFICIENT_VALUE),
        );
        return;
      }

      phase2RequestRef.current = false;
      await runScoringFlow({
        hasFreshGuaranteeImage: true,
        freshGuaranteeImage,
      });
    } catch (error) {
      if (!mountedRef.current) return;
      setTechnicalError(error);
    } finally {
      phase2RequestRef.current = false;
      if (mountedRef.current) {
        setIsSubmittingPhase2(false);
      }
    }
  };

  const handlePhotosChange = (photos) => {
    updatePhase2({
      photos,
      guaranteeValidationResult: null,
    });
  };

  const handleClearPhotos = () => {
    updatePhase2({
      photos: createEmptyPhotos(),
      guaranteeValidationResult: null,
      guaranteeImage: null,
      preOffer: phase2.preOffer
        ? {
          ...phase2.preOffer,
          guaranteeImage: null,
          useGuaranteeImage: true,
        }
        : null,
    });
    if (applicationId) {
      persistPhase3Snapshot({ guaranteeImage: null });
    }
  };

  const handleModifyTerm = () => {
    setFlowProgress((current) => ({
      ...current,
      isModifyingTerm: true,
    }));
    navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.APPLICABILITY_LOAN));
  };

  const handlePreOfferFinish = async () => {
    if (
      !phase2.selectedVisitDate
      || !phase2.selectedVisitTimeSlot
      || phase3RequestRef.current
      || !applicationId
    ) {
      return;
    }

    const scheduledVisitAt = buildScheduledVisitAt(
      phase2.selectedVisitDate,
      phase2.selectedVisitTimeSlot,
    );
    const acceptedAmount = Number(
      phase2.preOffer?.amount ?? phase3Snapshot?.acceptedAmount,
    );
    if (!scheduledVisitAt || !Number.isFinite(acceptedAmount) || acceptedAmount <= 0) {
      return;
    }

    phase3RequestRef.current = true;
    setIsSubmittingPhase3(true);
    try {
      const preapproval = await dispatch(acceptPreapproval(applicationId, {
        acceptedAmount,
      }));
      if (!mountedRef.current) return;

      const visit = await dispatch(scheduleVisit(applicationId, {
        scheduledVisitAt,
      }));
      if (!mountedRef.current) return;

      persistPhase3Snapshot({
        currentPhase: visit.currentPhase || preapproval.currentPhase,
        currentStep: visit.currentStep || preapproval.currentStep,
        acceptedAmount: preapproval.preapprovalAmount ?? acceptedAmount,
        suggestedLoanAmount: acceptedAmount,
        requestedTerm: phase2.preOffer?.term ?? phase3Snapshot?.requestedTerm,
        paymentFrequency: phase2.preOffer?.paymentFrequency
          || phase3Snapshot?.paymentFrequency,
        paymentDay: phase2.preOffer?.paymentDay || phase3Snapshot?.paymentDay,
        guaranteeName: phase2.preOffer?.guaranteeName
          || phase3Snapshot?.guaranteeName,
        guaranteeDescription: phase3Snapshot?.guaranteeDescription
          || resolveGuaranteeDescription(phase2)
          || null,
        guaranteeImage: phase2.preOffer?.guaranteeImage
          || phase2.guaranteeImage
          || phase3Snapshot?.guaranteeImage
          || null,
        scheduledVisitAt: resolveScheduledVisitAt(
          visit.scheduledVisitAt,
          scheduledVisitAt,
        ),
        rescheduleCount: visit.rescheduleCount ?? 0,
        maxReschedules: visit.maxReschedules
          ?? preapproval.maxReschedules
          ?? null,
        localVisitStatus: LOCAL_VISIT_STATUS.SCHEDULED,
        preapprovalAcceptedAt: preapproval.preapprovalAcceptedAt || null,
      });

      resetFlowToHome();
    } catch (error) {
      if (!mountedRef.current) return;
      setTechnicalError(error);
    } finally {
      phase3RequestRef.current = false;
      if (mountedRef.current) {
        setIsSubmittingPhase3(false);
      }
    }
  };

  const handleRescheduleVisit = async (visitDate, visitTimeSlot) => {
    const newVisitAt = buildScheduledVisitAt(visitDate, visitTimeSlot);
    if (!applicationId || !newVisitAt) {
      throw new Error('Missing visit data');
    }

    setIsSubmittingPhase3(true);
    try {
      const visit = await dispatch(rescheduleVisit(applicationId, { newVisitAt }));
      if (!mountedRef.current) return;

      persistPhase3Snapshot({
        scheduledVisitAt: resolveScheduledVisitAt(
          visit.scheduledVisitAt,
          newVisitAt,
        ),
        rescheduleCount: visit.rescheduleCount
          ?? ((phase3Snapshot?.rescheduleCount || 0) + 1),
        maxReschedules: visit.maxReschedules ?? phase3Snapshot?.maxReschedules,
        currentPhase: visit.currentPhase || phase3Snapshot?.currentPhase,
        currentStep: visit.currentStep || phase3Snapshot?.currentStep,
        localVisitStatus: LOCAL_VISIT_STATUS.RESCHEDULED,
      });
      setPhase3Status((current) => ({
        ...(current || {}),
        activeApplication: {
          ...(current?.activeApplication || {}),
          applicationId,
          currentPhase: visit.currentPhase
            || current?.activeApplication?.currentPhase,
          currentStep: visit.currentStep
            || current?.activeApplication?.currentStep,
        },
        gestoria: {
          ...(current?.gestoria || {}),
          scheduledVisitAt: resolveScheduledVisitAt(
            visit.scheduledVisitAt,
            newVisitAt,
          ),
          rescheduleCount: visit.rescheduleCount
            ?? ((current?.gestoria?.rescheduleCount || 0) + 1),
          maxReschedules: visit.maxReschedules
            ?? current?.gestoria?.maxReschedules,
          dispatchedAt: visit.dispatchedAt
            || current?.gestoria?.dispatchedAt
            || null,
        },
      }));
      updatePhase2({
        selectedVisitDate: null,
        selectedVisitTimeSlot: null,
      });
    } catch (error) {
      if (mountedRef.current) {
        setTechnicalError(error);
      }
      throw error;
    } finally {
      if (mountedRef.current) {
        setIsSubmittingPhase3(false);
      }
    }
  };

  const handleCancelVisit = async () => {
    if (!applicationId) {
      throw new Error('Missing applicationId');
    }

    setIsSubmittingPhase3(true);
    try {
      const visit = await dispatch(cancelVisit(applicationId, {}));
      if (!mountedRef.current) return;

      persistPhase3Snapshot({
        scheduledVisitAt: resolveScheduledVisitAt(
          visit.scheduledVisitAt,
          phase3Snapshot?.scheduledVisitAt,
        ),
        rescheduleCount: visit.rescheduleCount
          ?? phase3Snapshot?.rescheduleCount,
        maxReschedules: visit.maxReschedules
          ?? phase3Snapshot?.maxReschedules,
        currentPhase: visit.currentPhase || phase3Snapshot?.currentPhase,
        currentStep: visit.currentStep || phase3Snapshot?.currentStep,
        localVisitStatus: LOCAL_VISIT_STATUS.CANCELLED,
      });
      setPhase3Status((current) => ({
        ...(current || {}),
        gestoria: {
          ...(current?.gestoria || {}),
          scheduledVisitAt: resolveScheduledVisitAt(
            visit.scheduledVisitAt,
            current?.gestoria?.scheduledVisitAt,
          ),
          rescheduleCount: visit.rescheduleCount
            ?? current?.gestoria?.rescheduleCount,
          maxReschedules: visit.maxReschedules
            ?? current?.gestoria?.maxReschedules,
        },
      }));
      updatePhase2({
        selectedVisitDate: null,
        selectedVisitTimeSlot: null,
      });
    } catch (error) {
      if (mountedRef.current) {
        setTechnicalError(error);
      }
      throw error;
    } finally {
      if (mountedRef.current) {
        setIsSubmittingPhase3(false);
      }
    }
  };

  const handleLayoutBack = () => {
    if (activeLegalDocument) {
      setActiveLegalDocument(null);
      return;
    }

    if (
      showVerificationLoader
      || isSubmittingQuestionnaire
      || isSubmittingPhase2
      || isSubmittingPhase3
    ) {
      return;
    }

    if (
      step === DIGITAL_LOAN_STEPS.INTRO
      || step === DIGITAL_LOAN_STEPS.PRE_OFFER
      || step === DIGITAL_LOAN_STEPS.VISIT_STATUS
      || (flowProgress.phase3Resume && isPhase3Step(step))
      || (flowProgress.phase4Resume && isPhase4Step(step))
    ) {
      resetFlowToHome();
      return;
    }

    if (
      step === DIGITAL_LOAN_STEPS.GUARANTEE_MODEL
      && isManualCatalogDetailLevel(phase2.manualCatalogStartLevel)
    ) {
      const backStep = getManualCatalogBackStep(phase2.manualCatalogStartLevel);
      setAllowManualModel(false);
      setPhase2((current) => clearManualCatalogFromLevel(current));
      navigate(ROUTE_PATH.setDigitalLoan(backStep));
      return;
    }

    if (isPhase2Step(step)) {
      navigate(ROUTE_PATH.setDigitalLoan(getPreviousStep(step)));
      return;
    }

    if (
      step === DIGITAL_LOAN_STEPS.APPLICABILITY_LOAN
      || step === DIGITAL_LOAN_STEPS.APPLICABILITY_ADDRESS
      || step === DIGITAL_LOAN_STEPS.APPLICABILITY_IDENTITY
    ) {
      if (
        flowProgress.isModifyingTerm
        && step === DIGITAL_LOAN_STEPS.APPLICABILITY_LOAN
      ) {
        navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.NOT_PREAPPROVED));
        return;
      }
      handleQuestionnaireBack();
      return;
    }

    if (step === DIGITAL_LOAN_STEPS.PERMISSIONS) {
      handlePermissionsBack();
      return;
    }

    if (step === DIGITAL_LOAN_STEPS.LOCATION) {
      handleLocationBack();
    }
  };

  const isQuestionnaireLocked = flowProgress.questionnaireCompleted && !flowProgress.isModifyingTerm;

  let modelSelectId = '';
  if (!allowManualModel) {
    if (phase2.selectedModel?.id === MANUAL_MODEL_CODE) {
      modelSelectId = OTHER_OPTION_ID;
    } else {
      modelSelectId = phase2.selectedModel?.id || '';
    }
  }

  const visitCalendar = useMemo(() => buildVisitCalendar(), []);
  const phase3Display = useMemo(
    () => normalizePhase3Status(phase3Status, phase3Snapshot),
    [phase3Status, phase3Snapshot],
  );
  const preOfferDisplay = useMemo(
    () => mergePreOfferDisplay(phase2.preOffer, phase3Display),
    [phase2.preOffer, phase3Display],
  );
  const maxReschedules = resolveMaxReschedules(phase3Display);
  const rescheduleCount = Number(phase3Display?.rescheduleCount) || 0;
  const isVisitCancelled = (
    phase3Display?.localVisitStatus === LOCAL_VISIT_STATUS.CANCELLED
  );
  const isWithinCutoff = isVisitWithinHours(
    phase3Display?.scheduledVisitAt,
    VISIT_MODIFY_CUTOFF_HOURS,
  );
  const maxReschedulesReached = (
    !isVisitCancelled
    && rescheduleCount >= maxReschedules
  );
  const canReschedule = (
    (isVisitCancelled || !maxReschedulesReached)
    && (isVisitCancelled || !isWithinCutoff)
  );
  const canCancelVisit = !isVisitCancelled && !isWithinCutoff;

  const catalogLiterals = {
    title: literals.phase2?.catalog?.title,
    subtitle: literals.phase2?.catalog?.subtitle,
    note: literals.phase2?.catalog?.note,
    back: literals.phase2?.common?.back,
    loading: literals.phase2?.common?.loading,
  };

  const renderStep = () => {
    if (isPhase4Step(permittedStep)) {
      return (
        <Phase4Wizard
          step={permittedStep}
          applicationId={applicationId}
          onPhase3={() => {
            navigate(ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.VISIT_STATUS));
          }}
          onHome={resetFlowToHome}
        />
      );
    }

    switch (permittedStep) {
      case DIGITAL_LOAN_STEPS.INTRO:
        return (
          <IntroScreen
            literals={literals.intro}
            onContinue={handleIntroContinue}
          />
        );
      case DIGITAL_LOAN_STEPS.PERMISSIONS:
        return (
          <>
            <PermissionsScreen
              literals={literals.permissions}
              permissions={permissions}
              onPermissionChange={handlePermissionChange}
              onAccept={handlePermissionsAccept}
              onBack={handlePermissionsBack}
              onOpenDocument={handleOpenLegalDocument}
            />
            {activeLegalDocument && DIGITAL_LOAN_LEGAL_DOCUMENTS[activeLegalDocument] && (
              <LegalDocumentViewer
                document={{
                  id: DIGITAL_LOAN_LEGAL_DOCUMENTS[activeLegalDocument].id,
                  title: literals.permissions.labels[activeLegalDocument],
                  file: DIGITAL_LOAN_LEGAL_DOCUMENTS[activeLegalDocument].file,
                }}
                onClose={handleCloseLegalDocument}
              />
            )}
          </>
        );
      case DIGITAL_LOAN_STEPS.LOCATION:
        return (
          <LocationScreen
            literals={literals.location}
            initialValues={questionnaire}
            onValidate={handleLocationValidate}
            onBack={handleLocationBack}
            isSubmitting={isValidatingCoordinates}
          />
        );
      case DIGITAL_LOAN_STEPS.APPLICABILITY_LOAN:
        return (
          <ApplicabilityLoanScreen
            literals={questionnaireLiterals.loan}
            questionnaire={questionnaire}
            onChange={updateQuestionnaire}
            onNext={handleLoanNext}
            onBack={handleLayoutBack}
            disabled={isQuestionnaireLocked}
          />
        );
      case DIGITAL_LOAN_STEPS.APPLICABILITY_ADDRESS:
        return (
          <ApplicabilityAddressScreen
            literals={questionnaireLiterals.address}
            questionnaire={questionnaire}
            onAddressChange={updateQuestionnaireAddress}
            onProofChange={(proofOfAddress) => updateQuestionnaire({ proofOfAddress })}
            onNext={handleAddressNext}
            onBack={handleQuestionnaireBack}
            disabled={isQuestionnaireLocked}
          />
        );
      case DIGITAL_LOAN_STEPS.APPLICABILITY_IDENTITY:
        return (
          <ApplicabilityIdentityScreen
            literals={questionnaireLiterals.identity}
            questionnaire={questionnaire}
            onChange={updateQuestionnaire}
            onSubmit={handleQuestionnaireSubmit}
            onBack={handleQuestionnaireBack}
            isSubmitting={isSubmittingQuestionnaire}
            disabled={isQuestionnaireLocked}
          />
        );
      case DIGITAL_LOAN_STEPS.GUARANTEE_AVAILABILITY:
        return (
          <GuaranteeAvailabilityScreen
            literals={literals.phase2.guaranteeAvailability}
            onHasProduct={handleHasGuaranteeProduct}
            onNoProductUnderstood={resetFlowToHome}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.GUARANTEE_FAMILY:
        return (
          <CatalogSelectionScreen
            literals={catalogLiterals}
            items={catalogItems}
            variant='grid'
            loading={catalogLoading}
            onSelect={handleFamilySelect}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.GUARANTEE_SUBFAMILY:
        return (
          <CatalogSelectionScreen
            literals={{
              ...catalogLiterals,
              subtitle: null,
              note: null,
            }}
            items={catalogItems}
            variant='list'
            loading={catalogLoading}
            onSelect={handleSubFamilySelect}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.GUARANTEE_ARTICLE:
        return (
          <CatalogSelectionScreen
            literals={{
              ...catalogLiterals,
              subtitle: null,
              note: null,
            }}
            items={catalogItems}
            variant='list'
            loading={catalogLoading}
            onSelect={handleArticleSelect}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.GUARANTEE_BRAND:
        return (
          <CatalogSelectionScreen
            literals={{
              ...catalogLiterals,
              subtitle: null,
              note: null,
            }}
            items={catalogItems}
            variant='list'
            loading={catalogLoading}
            onSelect={handleBrandSelect}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.GUARANTEE_MODEL:
        if (isManualCatalogDetailLevel(phase2.manualCatalogStartLevel)) {
          return (
            <ManualCatalogDetailScreen
              literals={literals.phase2.model}
              startLevel={phase2.manualCatalogStartLevel}
              values={phase2.manualCatalogValues || emptyManualCatalogValues()}
              productCondition={phase2.productCondition}
              estimatedValue={phase2.estimatedValue}
              onValuesChange={(patch) => setPhase2((current) => {
                const next = patchManualCatalogValues(current, patch);
                const resetsValue = (
                  patch.familyName != null
                  || patch.articleName != null
                  || patch.brandName != null
                );
                if (!resetsValue) return next;
                return {
                  ...next,
                  estimatedValue: '',
                  estimatedValueLocked: false,
                };
              })}
              onConditionChange={(productCondition) => updatePhase2({ productCondition })}
              onEstimatedValueChange={(estimatedValue) => updatePhase2({ estimatedValue })}
              onNext={handleModelNext}
              onBack={handleLayoutBack}
            />
          );
        }
        return (
          <ModelDetailScreen
            literals={literals.phase2.model}
            models={catalogItems}
            selectedModelId={modelSelectId}
            manualModelName={
              phase2.selectedModel?.id === MANUAL_MODEL_CODE
                ? phase2.selectedModel.name || ''
                : ''
            }
            allowManualModel={allowManualModel}
            productCondition={phase2.productCondition}
            estimatedValue={phase2.estimatedValue}
            estimatedValueLocked={!!phase2.estimatedValueLocked}
            loading={catalogLoading}
            onModelChange={(modelId) => {
              if (modelId === OTHER_OPTION_ID) {
                updatePhase2({
                  selectedModel: { id: MANUAL_MODEL_CODE, name: '' },
                  estimatedValue: '',
                  estimatedValueLocked: false,
                });
                return;
              }
              const model = catalogItems.find((item) => item.id === modelId);
              if (isUiOtherOption(model)) {
                updatePhase2({
                  selectedModel: { id: MANUAL_MODEL_CODE, name: '' },
                  estimatedValue: '',
                  estimatedValueLocked: false,
                });
                return;
              }
              updatePhase2({
                selectedModel: model || null,
                ...estimatedValueStateFromModel(model),
              });
            }}
            onManualModelChange={(name) => {
              const value = normalizeCollateralText(name);
              if (value.trim()) {
                updatePhase2({
                  selectedModel: { id: MANUAL_MODEL_CODE, name: value },
                  estimatedValueLocked: false,
                });
                return;
              }
              // Vacío: en fallback [] limpia; en Otro mantiene selección manual.
              updatePhase2({
                selectedModel: allowManualModel
                  ? null
                  : { id: MANUAL_MODEL_CODE, name: '' },
                estimatedValueLocked: false,
              });
            }}
            onConditionChange={(productCondition) => updatePhase2({ productCondition })}
            onEstimatedValueChange={(estimatedValue) => updatePhase2({ estimatedValue })}
            onNext={handleModelNext}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.PHOTO_REQUIREMENTS:
        return (
          <PhotoRequirementsScreen
            literals={literals.phase2.photos}
            consents={phase2.photoConsents}
            onConsentChange={handlePhotoConsentChange}
            onContinue={() => navigate(
              ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.PHOTO_CAPTURE),
            )}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.PHOTO_CAPTURE:
        return (
          <PhotoCaptureScreen
            literals={literals.phase2.photoCapture}
            photos={phase2.photos}
            requireDocument={!!phase2.photoConsents.hasInvoice}
            onPhotosChange={handlePhotosChange}
            onClearPhotos={handleClearPhotos}
            onReview={() => navigate(
              ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.PHOTO_REVIEW),
            )}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.PHOTO_REVIEW:
        return (
          <PhotoReviewScreen
            literals={literals.phase2.photoReview}
            photos={phase2.photos}
            requireDocument={!!phase2.photoConsents.hasInvoice}
            onValidate={handleValidateGuarantee}
            onClearPhotos={handleClearPhotos}
            onEdit={() => navigate(
              ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.PHOTO_CAPTURE),
            )}
            onBack={handleLayoutBack}
            isSubmitting={isSubmittingPhase2}
          />
        );
      case DIGITAL_LOAN_STEPS.VALIDATION_FAILED:
        return (
          <ValidationFailedScreen
            literals={literals.phase2.validationFailed}
            onRetakePhotos={() => navigate(
              ROUTE_PATH.setDigitalLoan(DIGITAL_LOAN_STEPS.PHOTO_CAPTURE),
            )}
            onChangePledge={selectOtherPledge}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.INSUFFICIENT_VALUE:
        return (
          <InsufficientValueScreen
            literals={literals.phase2.insufficientValue}
            onContinueWithLowerAmount={handleModifyTerm}
            onSelectOtherPledge={selectOtherPledge}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.MISSING_ONBOARDING:
        return (
          <MissingOnboardingScreen
            literals={literals.phase2.missingOnboarding}
            onCompleteIdentification={() => {
              navigate(
                ROUTE_PATH.setRegistration(REGISTRATION_STEPS.IDENTITY_VALIDATION),
              );
            }}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.NOT_PREAPPROVED:
        return (
          <NotPreapprovedScreen
            literals={literals.phase2.notPreapproved}
            summary={
              phase2.scoringResult?.summary || {
                amount: questionnaire.requestedAmount,
                term: questionnaire.paymentTerm,
                paymentFrequency: questionnaire.paymentFrequency,
                paymentDay: '14 de cada mes',
              }
            }
            onModifyTerm={handleModifyTerm}
            onSelectOtherPledge={selectOtherPledge}
            onBack={handleLayoutBack}
          />
        );
      case DIGITAL_LOAN_STEPS.PRE_OFFER:
        return (
          <PreOfferScreen
            literals={literals.phase2.preOffer}
            preOffer={preOfferDisplay}
            selectedVisitDate={phase2.selectedVisitDate}
            selectedVisitTimeSlot={phase2.selectedVisitTimeSlot}
            onVisitDateChange={(selectedVisitDate) => updatePhase2({
              selectedVisitDate,
              selectedVisitTimeSlot: null,
            })}
            onVisitTimeChange={(selectedVisitTimeSlot) => updatePhase2({
              selectedVisitTimeSlot,
            })}
            onFinish={handlePreOfferFinish}
            isSubmitting={isSubmittingPhase3}
          />
        );
      case DIGITAL_LOAN_STEPS.VISIT_STATUS:
        return (
          <VisitStatusScreen
            literals={literals.phase3.visitStatus}
            snapshot={phase3Display || {}}
            availableVisitDates={visitCalendar.availableVisitDates}
            availableVisitTimeSlots={visitCalendar.availableVisitTimeSlots}
            selectedVisitDate={phase2.selectedVisitDate}
            selectedVisitTimeSlot={phase2.selectedVisitTimeSlot}
            onVisitDateChange={(selectedVisitDate) => updatePhase2({
              selectedVisitDate,
              selectedVisitTimeSlot: null,
            })}
            onVisitTimeChange={(selectedVisitTimeSlot) => updatePhase2({
              selectedVisitTimeSlot,
            })}
            onReschedule={handleRescheduleVisit}
            onCancel={handleCancelVisit}
            isSubmitting={isSubmittingPhase3}
            canReschedule={canReschedule}
            canCancel={canCancelVisit}
            isWithinCutoff={isWithinCutoff}
            maxReschedulesReached={maxReschedulesReached}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Layout
      className='digital-loan-layout'
      onBack={handleLayoutBack}
      bottomMenu={false}
      bgWhite
    >
      <AppShell variant='default'>{renderStep()}</AppShell>

      {showBiometricModal && (
        <BiometricVerificationModal
          literals={literals.biometric}
          hasError={biometricError}
          onClose={closeBiometricModal}
          onRetry={handleBiometricRetry}
          onBack={handleBiometricBack}
        />
      )}

      {showVerificationLoader && (
        <VerificationLoaderModal literals={literals.verificationLoader} />
      )}

      {rejectionType && (
        <RejectionSheet
          type={rejectionType}
          literals={literals.rejection}
          onUnderstood={handleRejectionUnderstood}
        />
      )}

      {technicalError && (
        <ErrorModal
          description={technicalError}
          onClose={handleTechnicalErrorClose}
        />
      )}
    </Layout>
  );
};

export default DigitalLoan;
